import { QueryClient, QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { DocumentFindLeadsCachedQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

/** How often a `running` status is re-read while waiting for the subscription's terminal event. */
export const FIND_LEADS_RUNNING_POLL_MS = 30_000;

export const documentFindLeadsCachedQueryKey = (documentId: string): QueryKey => [
  "documentFindLeadsCached",
  documentId,
];

/**
 * Marks the document's run as started. The cached query never refetches on its own, so this is
 * how it learns a run began and turns the subscription on.
 */
export function writeFindLeadsRunning(
  queryClient: QueryClient,
  documentId: string,
  operationId: string,
) {
  queryClient.setQueryData(documentFindLeadsCachedQueryKey(documentId), {
    documentFindLeadsCached: { operationId, status: "running", result: null },
  });
}

export const documentFindLeadsCachedQuery = graphql(`
  query documentFindLeadsCached($documentId: String!) {
    documentFindLeadsCached(documentId: $documentId) {
      operationId
      status
      result {
        documentId
        documentName
        buyerName
        buyerId
        leads {
          leadTitle
          leadMotivation
          matchingText
          leadType
          pageNumbers
          createdLeadId
          chunkNumber
          locations {
            pageNumber
            bbox {
              x
              y
              width
              height
            }
          }
        }
      }
    }
  }
`);

export type DocumentFindLeadsCachedResponse =
  DocumentFindLeadsCachedQuery["documentFindLeadsCached"];
export type DocumentFindLeadsResult = NonNullable<DocumentFindLeadsCachedResponse["result"]>;
export type DocumentFindLeadsLead = DocumentFindLeadsResult["leads"][number];

export function useDocumentFindLeadsCached(
  documentId: string,
  options?: UseQueryOptions<
    DocumentFindLeadsCachedQuery,
    unknown,
    DocumentFindLeadsCachedQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    documentFindLeadsCachedQueryKey(documentId),
    documentFindLeadsCachedQuery,
    [{ documentId }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      // A failed run is only reported until its cooldown ends, so re-read it on mount rather
      // than serving the failure from cache for the rest of the session.
      refetchOnMount: (query) =>
        query.state.data?.documentFindLeadsCached.status === "failed" ? "always" : false,
      // The subscription is the fast path; polling recovers a terminal event that never arrived.
      refetchInterval: (data) =>
        data?.documentFindLeadsCached.status === "running" ? FIND_LEADS_RUNNING_POLL_MS : false,
      ...options,
    },
  );

  return { data: data?.documentFindLeadsCached, ...rest };
}
